import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  getActiveWorkspace,
  getActiveWorkspaceId,
  getWorkspaceSelectedAvatar,
  setWorkspace,
  setActiveWorkspaceAvatar,
} from "../lib/workspaceStore";
import { useDeviceProfile } from "../hooks/useDeviceProfile";
import { SoundVideo } from "../components/SoundVideo";

const personaPresets = [
  {
    name: "Growth Strategist",
    look_style: "brand strategist, refined, premium, calm authority, modern office",
    personality: "strategic, composed, persuasive",
    speaking_style: "clear, confident, executive",
    camera_presence: "premium in-house OmniMark spokesperson",
  },
  {
    name: "UGC Product Host",
    look_style: "creator host, product demo setup, relatable, bright studio",
    personality: "energetic, approachable, persuasive",
    speaking_style: "natural, quick, social-first",
    camera_presence: "phone-friendly product presenter",
  },
  {
    name: "Ad Performance Coach",
    look_style: "performance marketer, dashboard room, analytical, focused",
    personality: "decisive, data-led, practical",
    speaking_style: "direct, tactical, confident",
    camera_presence: "ad optimization expert explaining what to do next",
  },
  {
    name: "Luxury Brand Presenter",
    look_style: "premium brand consultant, elegant wardrobe, refined studio lighting",
    personality: "polished, calm, aspirational",
    speaking_style: "warm, premium, reassuring",
    camera_presence: "high-trust spokesperson for premium offers",
  },
  {
    name: "Cinematic Founder",
    look_style: "founder-led brand film, tailored casual wardrobe, cinematic window light",
    personality: "visionary, candid, grounded",
    speaking_style: "human, deliberate, story-led",
    camera_presence: "credible founder speaking directly to camera",
  },
  {
    name: "Lifestyle Travel Host",
    look_style: "editorial travel host, elevated casual wardrobe, destination lifestyle",
    personality: "curious, optimistic, polished",
    speaking_style: "warm, vivid, inviting",
    camera_presence: "premium destination and experience presenter",
  },
  {
    name: "Fitness Coach",
    look_style: "experienced wellness coach, clean activewear, bright training studio",
    personality: "motivating, practical, encouraging",
    speaking_style: "clear, upbeat, instructional",
    camera_presence: "friendly coach demonstrating a safe next step",
  },
  {
    name: "Real Estate Guide",
    look_style: "knowledgeable property advisor, contemporary smart-casual wardrobe, architectural setting",
    personality: "reassuring, observant, professional",
    speaking_style: "calm, specific, trustworthy",
    camera_presence: "local guide presenting spaces and value clearly",
  },
];

const scriptModes = [
  "Dropshipping product demo",
  "Founder intro",
  "Ad hook test",
  "Customer support answer",
  "Brand DNA explainer",
  "Campaign performance update",
  "Cinematic brand story",
  "How-it-works walkthrough",
  "Testimonial-style case study",
  "Launch announcement",
];

const shotStyles = [
  "Tight headshot",
  "Waist-up studio",
  "Standing product demo",
  "Phone selfie UGC",
  "Product demo table",
  "Podcast interview",
  "Executive briefing",
  "Editorial three-quarter portrait",
  "Environmental product story",
  "Cinematic establishing shot",
];

const lightingPresets = [
  "Softbox beauty light",
  "Window-light lifestyle",
  "Clean ecommerce sweep",
  "Cinematic contrast",
  "Bright creator studio",
  "Luxury editorial",
  "Golden-hour natural light",
  "Neon night contrast",
];

const backgroundPresets = [
  "Modern office",
  "Product table",
  "White studio sweep",
  "Retail shelf",
  "Home lifestyle room",
  "Luxury kitchen lifestyle",
  "Bright healthcare consultation room",
  "Downtown storefront exterior",
  "Premium automotive showroom",
  "Fitness and wellness studio",
  "Travel destination overlook",
  "Cinematic nighttime city",
  "Golden-hour city rooftop",
  "Rainy neon city street",
  "Coastal cliff sunrise",
  "Desert modern villa",
  "Forest cabin retreat",
  "Boutique hotel lobby",
  "Art-deco lounge",
  "Startup war room",
  "Chef's kitchen",
  "Museum gallery",
  "Theater backstage",
  "Sports arena tunnel",
  "Festival outdoor stage",
  "Mountain lodge window",
  "Garden party editorial",
  "Sci-fi product laboratory",
  "Library study",
  "Green screen",
];

const performancePresets = [
  "Natural TV spokesperson",
  "Warm lifestyle storyteller",
  "High-energy social creator",
  "Confident product demonstrator",
  "Premium luxury presenter",
  "Calm expert advisor",
  "Cinematic documentary host",
  "Editorial fashion narrator",
];

const videoObjectives = [
  "Create ad hook",
  "Explain offer",
  "Product demo",
  "Campaign update",
  "Customer FAQ",
  "Founder-style pitch",
  "Cinematic brand story",
  "Tutorial walkthrough",
];

const videoDurations = [30, 45, 60, 90];

const spokenLanguages = [
  ["en-US", "English (US) — default"],
  ["en-GB", "English (UK)"],
  ["es-US", "Spanish (US)"],
  ["es-ES", "Spanish (Spain)"],
  ["fr-FR", "French"],
  ["pt-BR", "Portuguese (Brazil)"],
  ["de-DE", "German"],
  ["it-IT", "Italian"],
  ["ja-JP", "Japanese"],
  ["ko-KR", "Korean"],
];

const motionProfiles = [
  ["natural_presenter", "Natural TV presenter"],
  ["walk_and_talk", "Walk and talk"],
  ["product_demo", "Full-body product demo"],
  ["energetic_creator", "Energetic creator"],
  ["luxury_presenter", "Luxury presenter"],
  ["documentary_host", "Cinematic documentary host"],
  ["tutorial_guide", "Guided product walkthrough"],
];

const gestureIntensities = ["restrained", "natural", "expressive"];
const locomotionPresets = ["stationary", "step_and_pivot", "walk_and_talk", "turn_to_product"];
const cameraMotionPresets = ["locked", "gentle_push_in", "lateral_track", "handheld_ugc", "slow_orbit", "over_shoulder_product"];
const cinematicQualityChecklist = [
  "Use a scene that supports the message; avoid scenery that competes with the offer.",
  "Keep spoken scripts short enough for the selected duration and one clear call to action.",
  "Cinematic mode is reviewed before delivery; it does not promise natural full-body locomotion.",
  "Do not use a real person’s likeness, voice, or performance without documented permission.",
];

export default function AvatarStudioPage() {
  const device = useDeviceProfile();
  const [activeWorkspace, setActiveWorkspace] = useState(getActiveWorkspace());
  const workspaceId = activeWorkspace?.id || getActiveWorkspaceId();
  const [workspaces, setWorkspaces] = useState([]);
  const [brandDna, setBrandDna] = useState(null);
  const [useWorkspaceContext, setUseWorkspaceContext] = useState(true);
  const [customDirection, setCustomDirection] = useState("");
  const [avatars, setAvatars] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [voices, setVoices] = useState([]);
  const [voiceRuntime, setVoiceRuntime] = useState(null);
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({
    name: "",
    gender: "female",
    age_range: "28-38",
    ethnicity: "",
    industry: "marketing",
    tone: "confident",
    look_style: personaPresets[0].look_style,
    personality: personaPresets[0].personality,
    speaking_style: personaPresets[0].speaking_style,
    camera_presence: personaPresets[0].camera_presence,
    language: "en-US",
  });
  const [scriptMode, setScriptMode] = useState(scriptModes[0]);
  const [shotStyle, setShotStyle] = useState(shotStyles[1]);
  const [lighting, setLighting] = useState(lightingPresets[0]);
  const [background, setBackground] = useState(backgroundPresets[0]);
  const [customScene, setCustomScene] = useState("");
  const [performanceStyle, setPerformanceStyle] = useState(performancePresets[0]);
  const [videoObjective, setVideoObjective] = useState(videoObjectives[0]);
  const [targetDuration, setTargetDuration] = useState(30);
  // Portrait is the only customer path with a completed quality review.
  // Keep higher-complexity modes opt-in until they pass their own gates.
  const [renderMode, setRenderMode] = useState("portrait");
  const [motionProfile, setMotionProfile] = useState("natural_presenter");
  const [gestureIntensity, setGestureIntensity] = useState("natural");
  const [locomotion, setLocomotion] = useState("stationary");
  const [cameraMotion, setCameraMotion] = useState("gentle_push_in");
  const [captionPlan, setCaptionPlan] = useState("Broadcast captions synchronized to the spoken words, with title-safe lower-third placement.");
  // Scene direction and spoken dialogue are deliberately separate. A customer
  // describing a joyful walk through a market should never make the avatar say
  // those production notes out loud.
  const [sceneBrief, setSceneBrief] = useState("");
  const [directorPlan, setDirectorPlan] = useState(null);
  const [directorBusy, setDirectorBusy] = useState(false);
  const [sceneMicStatus, setSceneMicStatus] = useState("idle");
  const [sceneMicPermissionPending, setSceneMicPermissionPending] = useState(false);
  const [script, setScript] = useState("");
  const recentJobsRef = useRef(null);
  const sceneRecognitionRef = useRef(null);

  const compact = device.isMobile || device.width < 920;

  const selectedImage = selectedAvatar?.avatar_image_url || selectedAvatar?.image_url || "";
  const selectedVoiceProfile = useMemo(
    () => voices.find((voice) => voice.voice_id === selectedVoiceId) || voices[0] || null,
    [selectedVoiceId, voices],
  );
  const avatarVoiceExpression = useMemo(() => {
    const value = String(selectedAvatar?.gender || selectedAvatar?.gender_presentation || "").toLowerCase();
    if (["female", "woman", "girl", "feminine"].includes(value)) return "feminine";
    if (["male", "man", "boy", "masculine"].includes(value)) return "masculine";
    return "";
  }, [selectedAvatar]);
  useEffect(() => {
    const avatarGender = String(selectedAvatar?.gender || selectedAvatar?.gender_presentation || "").toLowerCase();
    const expression = ["female", "woman", "girl", "feminine"].includes(avatarGender)
      ? "feminine"
      : ["male", "man", "boy", "masculine"].includes(avatarGender) ? "masculine" : "";
    if (!expression || !voices.length) return;
    const current = voices.find((voice) => voice.voice_id === selectedVoiceId);
    if (current?.gender_expression === expression) return;
    const compatible = voices.find((voice) => voice.gender_expression === expression);
    if (compatible) setSelectedVoiceId(compatible.voice_id);
  }, [selectedAvatar, selectedVoiceId, voices]);

  const workspaceBrief = useMemo(() => {
    if (!activeWorkspace) return "";
    const brand = brandDna || {};
    return [
      `Workspace: ${activeWorkspace.name}`,
      brand.business_type ? `Business type: ${brand.business_type}` : null,
      brand.offer ? `Offer: ${brand.offer}` : null,
      brand.website_url ? `Website/domain: ${brand.website_url}` : null,
      brand.audience ? `Audience: ${brand.audience}` : null,
      brand.tone ? `Tone: ${brand.tone}` : null,
      brand.differentiator ? `Differentiator: ${brand.differentiator}` : null,
      brand.compliance_notes ? `Compliance notes: ${brand.compliance_notes}` : null,
    ].filter(Boolean).join("\n");
  }, [activeWorkspace, brandDna]);

  const studioBrief = useMemo(() => {
    const avatarName = selectedAvatar?.name || draft.name || "new OmniMark avatar";
    const context = [
      useWorkspaceContext && workspaceBrief ? `Workspace intelligence:\n${workspaceBrief}` : null,
      customDirection.trim() ? `Custom direction:\n${customDirection.trim()}` : null,
      sceneBrief.trim() ? `Scene direction only (never spoken):\n${sceneBrief.trim()}` : null,
    ].filter(Boolean).join("\n\n");
    const voice = selectedVoiceProfile
      ? `${selectedVoiceProfile.name} (${selectedVoiceProfile.tone}, ${selectedVoiceProfile.texture})`
      : `${selectedAvatar?.voice_tone || draft.tone}`;
    const scene = customScene.trim() || background;
    const language = selectedAvatar?.language || draft.language || "en-US";
    return `OmniMark Avatar Studio: generate a high-quality ${targetDuration}-second ${renderMode.replaceAll("_", " ")} ${shotStyle.toLowerCase()} video with ${avatarName}. Spoken language: ${language}. Objective: ${videoObjective}. Mode: ${scriptMode}. Lighting: ${lighting}. Creative scene: ${scene}. Performance: ${performanceStyle}. Motion profile: ${motionProfile.replaceAll("_", " ")}. Gesture intensity: ${gestureIntensity}. Locomotion: ${locomotion.replaceAll("_", " ")}. Camera movement: ${cameraMotion.replaceAll("_", " ")}. Captions/graphics: ${captionPlan}. Voice: ${voice}. Personality: ${selectedAvatar?.personality || draft.personality}. ${context ? `${context}\n` : ""}Spoken dialogue only: ${script.trim() || "[No dialogue supplied — do not turn scene direction into speech.]"}`;
  }, [background, cameraMotion, captionPlan, customDirection, customScene, draft.language, draft.name, draft.personality, draft.tone, gestureIntensity, lighting, locomotion, motionProfile, performanceStyle, renderMode, sceneBrief, script, scriptMode, selectedAvatar, selectedVoiceProfile, shotStyle, targetDuration, useWorkspaceContext, videoObjective, workspaceBrief]);

  const spokenScript = useMemo(() => {
    return String(script || "").trim();
  }, [script]);

  function startSceneDictation() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) {
      setError("Voice-to-text is not supported in this browser. You can still type the scene direction.");
      return;
    }
    sceneRecognitionRef.current?.stop?.();
    const recognition = new Recognition();
    recognition.lang = selectedAvatar?.language || draft.language || "en-US";
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.onstart = () => setSceneMicStatus("listening");
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).slice(event.resultIndex).filter((result) => result.isFinal).map((result) => result[0]?.transcript || "").join(" ").trim();
      if (transcript) setSceneBrief((current) => [current.trim(), transcript].filter(Boolean).join(current.trim() ? " " : ""));
    };
    recognition.onerror = () => setError("OmniMark could not hear that direction. Check microphone permission and try again.");
    recognition.onend = () => setSceneMicStatus("idle");
    sceneRecognitionRef.current = recognition;
    try { recognition.start(); } catch (_) { setSceneMicStatus("idle"); setError("OmniMark microphone could not start. Try again or type the scene direction."); }
  }

  function requestSceneDictation() {
    setSceneMicPermissionPending(true);
  }

  function stopSceneDictation() {
    sceneRecognitionRef.current?.stop?.();
  }

  async function directScene() {
    if (!workspaceId) {
      setError("Select a workspace before directing a scene.");
      return;
    }
    if (!sceneBrief.trim()) {
      setError("Describe the scene first. This direction will stay visual and will not be spoken in the video.");
      return;
    }
    setDirectorBusy(true);
    setError("");
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/avatar/director/brief`, {
        scene_brief: sceneBrief,
        avatar_name: selectedAvatar?.name || draft.name || "OmniMark avatar",
        avatar_voice_tone: selectedAvatar?.voice_tone || draft.tone,
        language: selectedAvatar?.language || draft.language || "en-US",
        objective: videoObjective,
        render_mode: renderMode,
        duration_seconds: targetDuration,
      });
      const plan = res.data || {};
      setDirectorPlan(plan);
      if (plan.scene) setCustomScene(plan.scene);
      if (plan.lighting && lightingPresets.includes(plan.lighting)) setLighting(plan.lighting);
      if (plan.shot_style && shotStyles.includes(plan.shot_style)) setShotStyle(plan.shot_style);
      if (plan.performance && performancePresets.includes(plan.performance)) setPerformanceStyle(plan.performance);
      if (plan.gesture_intensity && gestureIntensities.includes(plan.gesture_intensity)) setGestureIntensity(plan.gesture_intensity);
      if (plan.locomotion && locomotionPresets.includes(plan.locomotion)) setLocomotion(plan.locomotion);
      if (plan.camera_motion && cameraMotionPresets.includes(plan.camera_motion)) setCameraMotion(plan.camera_motion);
      setNotice("OmniScene Director prepared visual direction. It did not write or change the spoken dialogue.");
    } catch (err) {
      setError(err?.response?.data?.detail || "OmniScene Director could not prepare the scene yet.");
    } finally {
      setDirectorBusy(false);
    }
  }

  function buildAdCreatorHandoff(job = null) {
    const playbackUrl = job?.playback_url || job?.output?.omnimark_video_url || job?.output?.video_url || "";
    const avatar = selectedAvatar || {};
    return {
      source: "avatar_studio",
      workspace_id: workspaceId || activeWorkspace?.id || "",
      workspace_name: activeWorkspace?.name || "",
      selected_avatar: {
        id: avatar.id || "",
        slug: avatar.slug || "",
        name: avatar.name || draft.name || "OmniMark Avatar",
        image_url: avatar.avatar_image_url || avatar.image_url || selectedImage || "",
        voice_tone: avatar.voice_tone || draft.tone || "",
        voice_profile: selectedVoiceProfile,
        look_style: avatar.look_style || draft.look_style || "",
        personality: avatar.personality || draft.personality || "",
      },
      video: job ? {
        job_id: job.id,
        status: job.status,
        url: playbackUrl,
        provider: job.provider || job.output?.provider || "omnimark_in_house",
      } : null,
      creative: {
        brief: studioBrief,
        script,
        script_mode: scriptMode,
        shot_style: shotStyle,
        lighting,
        background,
        custom_scene: customScene,
        performance_style: performanceStyle,
        video_objective: videoObjective,
        caption_plan: captionPlan,
        voice_profile: selectedVoiceProfile,
        workspace_brief: workspaceBrief,
        custom_direction: customDirection,
      },
      created_at: new Date().toISOString(),
    };
  }

  function sendToAdCreator(job = null) {
    localStorage.setItem("omnimark_ad_creator_handoff", JSON.stringify(buildAdCreatorHandoff(job)));
    setNotice(job ? "Video copied to Ad Creator." : "Avatar brief copied to Ad Creator.");
  }

  function scrollToRecentJobs() {
    window.setTimeout(() => {
      recentJobsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
  }

  function readableJobError(errorText) {
    const text = String(errorText || "").trim();
    if (!text) return "";
    if (text.includes("requires completed OmniMark in-house voice audio")) {
      return "OmniMark needs the in-house voice runtime to create a talking avatar video. No silent placeholder video was created.";
    }
    if (text.includes("requires OMNIMARK_TALKING_HEAD_BIN") || text.includes("talking video requires")) {
      return "OmniMark needs the in-house talking-head runtime before this avatar can move and speak on video.";
    }
    if (text.includes("real avatar portrait image")) {
      return "This avatar is missing its portrait image. Restore or regenerate the avatar portrait, then create the video again.";
    }
    if (text.includes("timed out") && text.toLowerCase().includes("ffmpeg")) {
      return "OmniMark in-house video render timed out. Refresh the job, or delete it and generate again with a shorter script.";
    }
    if (text.includes("Command '['ffmpeg'") || text.includes("Command '[\"ffmpeg\"")) {
      return "OmniMark in-house video render failed while building the final MP4.";
    }
    if (text.includes("voiceprint_seed")) {
      return "OmniMark voice profile was missing a voice seed. Generate again to use the repaired voice path.";
    }
    return text.length > 220 ? `${text.slice(0, 220)}...` : text;
  }

  const loadAvatars = useCallback(async () => {
    setError("");
    try {
      const res = await api.get("/api/avatars?limit=36&offset=0");
      const items = res.data.items || [];
      setAvatars(items);
      const saved = getWorkspaceSelectedAvatar();
      const match = saved?.id ? items.find((item) => item.id === saved.id) : null;
      setSelectedAvatar(match || items[0] || saved || null);
    } catch (err) {
      setError("Failed to load in-house avatars.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadJobs = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await api.get(`/api/workspaces/${workspaceId}/avatar/jobs`);
      setJobs(res.data.items || []);
    } catch {
      setJobs([]);
    }
  }, [workspaceId]);

  const loadVoices = useCallback(async () => {
    try {
      const res = await api.get("/api/voices");
      const items = res.data.items || [];
      setVoices(items);
      setVoiceRuntime({
        provider: res.data.provider,
        engine: res.data.engine,
        available: Boolean(res.data.runtime_available),
        commercialMusicAvailable: Boolean(res.data.commercial_music_available),
        render_credit_costs: res.data.render_credit_costs || {},
        omnimotion: res.data.omnimotion || { available: false },
      });
      setSelectedVoiceId((current) => current || items[0]?.voice_id || "");
    } catch {
      setVoices([]);
      setVoiceRuntime(null);
    }
  }, []);

  useEffect(() => {
    loadAvatars();
    loadJobs();
    loadVoices();
  }, [loadAvatars, loadJobs, loadVoices]);

  useEffect(() => {
    if (!jobs.some((job) => job.status === "processing")) return undefined;
    const timer = window.setInterval(loadJobs, 8000);
    return () => window.clearInterval(timer);
  }, [jobs, loadJobs]);

  useEffect(() => {
    async function loadWorkspaces() {
      try {
        const res = await api.get("/api/workspaces");
        const items = res.data.items || [];
        setWorkspaces(items);
        if (!activeWorkspace && items.length > 0) {
          setWorkspace(items[0]);
          setActiveWorkspace(items[0]);
        }
      } catch {
        setWorkspaces([]);
      }
    }
    loadWorkspaces();
  }, [activeWorkspace]);

  useEffect(() => {
    if (!activeWorkspace?.id) {
      setBrandDna(null);
      return;
    }
    let alive = true;
    setWorkspace(activeWorkspace);
    api.get(`/api/workspaces/${activeWorkspace.id}/brand-dna`)
      .then((res) => {
        if (alive) setBrandDna(res.data || null);
      })
      .catch(() => {
        if (alive) setBrandDna(null);
      });
    return () => {
      alive = false;
    };
  }, [activeWorkspace]);

  function applyPreset(preset) {
    setDraft((current) => ({
      ...current,
      look_style: preset.look_style,
      personality: preset.personality,
      speaking_style: preset.speaking_style,
      camera_presence: preset.camera_presence,
    }));
    setNotice(`${preset.name} persona loaded.`);
  }

  async function createCustomAvatar() {
    if (!draft.name.trim()) {
      setError("Add an avatar name first.");
      return;
    }
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      const res = await api.post("/api/avatars/generate", {
        count: 1,
        name: draft.name.trim(),
        gender: draft.gender,
        age_range: draft.age_range,
        ethnicity: draft.ethnicity,
        industry: draft.industry,
        tone: draft.tone,
        look_style: draft.look_style,
        personality: draft.personality,
        speaking_style: draft.speaking_style,
        camera_presence: draft.camera_presence,
        language: draft.language,
      });
      await loadAvatars();
      const created = res.data.items?.[0];
      if (!created?.avatar_image_url && !created?.image_url) {
        throw new Error("Avatar image generation completed without a usable image.");
      }
      setSelectedAvatar(created);
      setNotice(`${created?.name || draft.name} created and added to Avatar Library.`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create in-house avatar.");
    } finally {
      setGenerating(false);
    }
  }

  async function generateReadyMade() {
    setGenerating(true);
    setError("");
    setNotice("");
    try {
      await api.post("/api/avatars/generate", {
        count: 3,
        industry: draft.industry || "marketing",
        style: draft.look_style || "modern professional",
        tone: draft.tone || "confident",
      });
      await loadAvatars();
      setNotice("Generated 3 new ready-made OmniMark avatars.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to generate ready-made avatars.");
    } finally {
      setGenerating(false);
    }
  }

  async function handleUseAvatar(avatar = selectedAvatar) {
    if (!avatar) return;
    if (!workspaceId) {
      setError("Select a workspace before using an avatar.");
      return;
    }
    try {
      const res = await api.post("/api/workspaces/select-avatar", {
        workspace_id: workspaceId,
        avatar_id: avatar.id,
      });
      setActiveWorkspaceAvatar({
        id: res.data.selected_avatar_id,
        name: res.data.selected_avatar_name,
        image_url: res.data.selected_avatar_image_url || avatar.avatar_image_url || avatar.image_url || "",
      });
      setSelectedAvatar(avatar);
      setNotice(`${res.data.selected_avatar_name} is now active for this workspace.`);
    } catch (err) {
      setError("Failed to save avatar to workspace.");
    }
  }

  async function createVideoJob() {
    if (!workspaceId) {
      setError("Select a workspace before creating avatar videos.");
      return;
    }
    if (!selectedAvatar) {
      setError("Create or select an avatar before starting a video.");
      return;
    }
    if (!selectedImage) {
      setError("This avatar has no generated image. Create a new avatar before starting a video.");
      return;
    }
    if (!selectedVoiceProfile) {
      setError("No compatible OmniMark voice is available for this avatar.");
      return;
    }
    if (!spokenScript) {
      setError("Add spoken dialogue before rendering. Scene direction stays visual and is never converted into an avatar voice line.");
      return;
    }
    setVideoBusy(true);
    setError("");
    setNotice("");
    try {
      let portraitReadyAvatar = selectedAvatar;
      if (renderMode === "portrait") {
        setNotice("Preparing this avatar's high-resolution face reference before speech rendering…");
        const prepared = await api.post(`/api/avatars/${selectedAvatar.id}/prepare-face-reference`);
        portraitReadyAvatar = {
          ...selectedAvatar,
          avatar_face_reference_url: prepared.data?.face_reference_url || "",
        };
        setSelectedAvatar(portraitReadyAvatar);
      }
      const res = await api.post(`/api/workspaces/${workspaceId}/avatar/render`, {
        presenter_key: portraitReadyAvatar?.slug || portraitReadyAvatar?.id || "omnimark_avatar",
        script: spokenScript,
        style: `${shotStyle}; ${lighting}; ${customScene.trim() || background}; ${performanceStyle}; ${videoObjective}; ${captionPlan}`,
        scene: {
          preset: background,
          custom_direction: customScene.trim(),
          lighting,
          shot_style: shotStyle,
        },
        performance: {
          preset: performanceStyle,
          objective: videoObjective,
          movement: "natural head, eye, expression, posture, and contextual hand gestures",
          motion_profile: motionProfile,
          gesture_intensity: gestureIntensity,
          locomotion,
          camera_motion: cameraMotion,
        },
        creative_brief: studioBrief,
        director_plan: directorPlan,
        scene_brief: sceneBrief,
        user_direction: script,
        custom_direction: customDirection,
        workspace_brief: workspaceBrief,
        video_objective: videoObjective,
        target_duration_seconds: targetDuration,
        render_mode: renderMode,
        script_mode: scriptMode,
        voice_id: selectedVoiceProfile?.voice_id || "",
        voice_profile: selectedVoiceProfile,
        language: selectedAvatar?.language || draft.language || "en-US",
      });
      await loadJobs();
      scrollToRecentJobs();
      if (res.data?.status === "failed") {
        setError(readableJobError(res.data?.provider_response?.error) || "Avatar video failed. Review the job below.");
      } else {
        setNotice("Avatar video is queued. You can leave this page; it will keep rendering and refresh here automatically. Credits are charged only after QA passes.");
      }
    } catch (err) {
      setError(readableJobError(err?.response?.data?.detail) || "Avatar video render is not ready yet, but your studio brief is prepared.");
      scrollToRecentJobs();
    } finally {
      setVideoBusy(false);
    }
  }

  async function syncVideoJob(jobId) {
    if (!workspaceId || !jobId) return;
    setNotice("");
    setError("");
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/avatar/jobs/${jobId}/sync`);
      await loadJobs();
      setNotice(res.data?.message || "Video status refreshed.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not refresh this video job yet.");
    }
  }

  async function deleteVideoJob(jobId) {
    if (!workspaceId || !jobId) return;
    if (!window.confirm("Delete this avatar video job and its generated media?")) return;
    setNotice("");
    setError("");
    try {
      await api.delete(`/api/workspaces/${workspaceId}/avatar/jobs/${jobId}`);
      setJobs((current) => current.filter((job) => job.id !== jobId));
      setNotice("Avatar video deleted.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not delete this avatar video.");
    }
  }

  async function copyBrief() {
    try {
      await navigator.clipboard.writeText(studioBrief);
      setNotice("Avatar studio brief copied.");
    } catch {
      setError("Could not copy brief. Select the text manually.");
    }
  }

  if (loading) {
    return <div style={page}>Loading Avatar Studio...</div>;
  }

  return (
    <div style={page} data-testid="avatar-studio-page">
      <section style={compact ? heroCompact : hero}>
        <div style={heroCopy}>
          <div style={eyebrow}>OmniMark in-house avatar studio</div>
          <h1 style={title}>Create the exact AI spokesperson your brand needs.</h1>
          <p style={subtitle}>
            Build new in-house avatars, choose real OmniMark-generated presenters, write scripts, prepare camera-ready video briefs, and send the best avatar into ads, AI Studio, and campaigns.
          </p>
          <div style={actions}>
            <button type="button" onClick={createCustomAvatar} disabled={generating} style={button}>
              {generating ? "Creating..." : "Create Avatar"}
            </button>
            <button type="button" onClick={generateReadyMade} disabled={generating} style={outlineButton}>
              Generate 3 Ready-made
            </button>
            <Link to="/app/avatars" style={outlineButtonDark}>Open Library</Link>
          </div>
        </div>
        <div style={previewHero}>
          {selectedImage ? (
            <img src={selectedImage} alt={selectedAvatar?.name || "Selected avatar"} style={heroImage} />
          ) : (
            <div style={emptyPortrait}>OM</div>
          )}
          <div style={heroAvatarInfo}>
            <strong>{selectedAvatar?.name || "No avatar selected"}</strong>
            <span>{selectedAvatar?.look_style || draft.look_style}</span>
          </div>
        </div>
      </section>

      {error && <div style={errorBox}>{error}</div>}
      {notice && <div style={noticeBox}>{notice}</div>}

      <section style={workspacePanel}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>Workspace Video Direction</div>
            <div style={muted}>OmniMark can generate avatars, scripts, and videos from this workspace's Brand DNA, or you can give a different direction for this one video.</div>
          </div>
          <div style={actions}>
            {workspaces.length > 0 && (
              <select
                value={workspaceId || ""}
                onChange={(e) => {
                  const next = workspaces.find((item) => item.id === e.target.value);
                  if (next) {
                    setWorkspace(next);
                    setActiveWorkspace(next);
                    setNotice(`${next.name} is now active for Avatar Studio.`);
                  }
                }}
                style={selectInput}
              >
                {workspaces.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            )}
            <button type="button" onClick={() => setUseWorkspaceContext((value) => !value)} style={useWorkspaceContext ? button : outlineButton}>
              {useWorkspaceContext ? "Using workspace" : "Workspace off"}
            </button>
          </div>
        </div>
        <div style={compact ? workspaceGridCompact : workspaceGrid}>
          <div style={workspaceBriefBox}>
            <strong>{activeWorkspace?.name || "No workspace selected"}</strong>
            <pre style={briefPre}>{workspaceBrief || "No Brand DNA saved yet. Add Brand DNA or use custom direction."}</pre>
          </div>
          <Field label="Choose something different for this video">
            <textarea
              value={customDirection}
              onChange={(e) => setCustomDirection(e.target.value)}
              placeholder="Example: Make this a fast UGC-style TikTok demo even though the brand normally sounds calm and professional."
              style={textareaTall}
            />
          </Field>
        </div>
      </section>

      <section style={compact ? mainGridCompact : mainGrid}>
        <div style={panel}>
          <div style={sectionHeader}>
            <div>
              <div style={sectionTitle}>Create In-house Avatar</div>
              <div style={muted}>Control the visual identity, voice, role, personality, and camera presence before OmniMark generates the avatar.</div>
            </div>
          </div>

          <div style={presetGrid}>
            {personaPresets.map((preset) => (
              <button key={preset.name} type="button" onClick={() => applyPreset(preset)} style={presetCard}>
                <strong>{preset.name}</strong>
                <span>{preset.camera_presence}</span>
              </button>
            ))}
          </div>

          <div style={formGrid}>
            <Field label="Avatar name">
              <input value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} placeholder="Luna Growth" style={input} />
            </Field>
            <Field label="Gender presentation">
              <select value={draft.gender} onChange={(e) => setDraft((p) => ({ ...p, gender: e.target.value }))} style={input}>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="person">Neutral</option>
              </select>
            </Field>
            <Field label="Age range">
              <input value={draft.age_range} onChange={(e) => setDraft((p) => ({ ...p, age_range: e.target.value }))} style={input} />
            </Field>
            <Field label="Ethnicity or identity">
              <input value={draft.ethnicity} onChange={(e) => setDraft((p) => ({ ...p, ethnicity: e.target.value }))} placeholder="Black, Latina, East Asian, diverse..." style={input} />
            </Field>
            <Field label="Industry">
              <input value={draft.industry} onChange={(e) => setDraft((p) => ({ ...p, industry: e.target.value }))} style={input} />
            </Field>
            <Field label="Voice tone">
              <input value={draft.tone} onChange={(e) => setDraft((p) => ({ ...p, tone: e.target.value }))} style={input} />
            </Field>
            <Field label="Spoken language">
              <select value={draft.language} onChange={(e) => setDraft((p) => ({ ...p, language: e.target.value }))} style={input}>
                {spokenLanguages.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </Field>
          </div>
          <div style={modeBanner}>English (US) is the default. Select the language before creating the avatar, then provide or generate a script in that language so the in-house voice runtime receives the right language preference.</div>

          <div style={wideFormGrid}>
            <Field label="Look, wardrobe, setting, role">
              <textarea value={draft.look_style} onChange={(e) => setDraft((p) => ({ ...p, look_style: e.target.value }))} style={textarea} />
            </Field>
            <Field label="Personality and camera presence">
              <textarea
                value={`${draft.personality}\n${draft.camera_presence}`}
                onChange={(e) => {
                  const [personality, ...presence] = e.target.value.split("\n");
                  setDraft((p) => ({ ...p, personality, camera_presence: presence.join(" ").trim() || p.camera_presence }));
                }}
                style={textarea}
              />
            </Field>
          </div>
        </div>

        <div style={panel}>
          <div style={sectionTitle}>Generate Avatar Video</div>
          <div style={muted}>Generate without using the camera. Choose the in-house avatar, video objective, shot style, lighting, background, captions, and script.</div>
          <div style={modeBanner}>
            <strong>No camera needed</strong>
            <span>Use this when customers want OmniMark to create a video from an avatar and script. Use AI Studio camera mode only when they want to record real footage.</span>
          </div>
          <Field label="Video objective">
            <select value={videoObjective} onChange={(e) => setVideoObjective(e.target.value)} style={input}>
              {videoObjectives.map((objective) => <option key={objective}>{objective}</option>)}
            </select>
          </Field>
          <Field label="Script mode">
            <select value={scriptMode} onChange={(e) => setScriptMode(e.target.value)} style={input}>
              {scriptModes.map((mode) => <option key={mode}>{mode}</option>)}
            </select>
          </Field>
          <Field label="Target length">
            <select value={targetDuration} onChange={(e) => setTargetDuration(Number(e.target.value))} style={input}>
              {videoDurations.map((seconds) => <option key={seconds} value={seconds}>{seconds} seconds</option>)}
            </select>
          </Field>
          <Field label="Render quality">
            <select value={renderMode} onChange={(e) => setRenderMode(e.target.value)} style={input}>
              <option value="cinematic">Cinematic scene assembly — visual QA required{voiceRuntime?.render_credit_costs?.cinematic?.[String(targetDuration)] !== undefined ? ` (${voiceRuntime.render_credit_costs.cinematic[String(targetDuration)]} credits)` : ""}</option>
              <option value="portrait">Portrait spokesperson — face and head animation{voiceRuntime?.render_credit_costs?.portrait?.[String(targetDuration)] ? ` (${voiceRuntime.render_credit_costs.portrait[String(targetDuration)]} credits)` : ""}</option>
            </select>
          </Field>
          <div style={productionNote}>Full-body walking, running, and complex action generation is in private technical validation and is not available to customers yet. OmniMark will only enable it after a repeatable head-to-toe source and visual quality gate pass.</div>
          <Field label="OmniMark voice">
            <select value={selectedVoiceId} onChange={(e) => setSelectedVoiceId(e.target.value)} style={input}>
              {voices.map((voice) => (
                <option key={voice.voice_id} value={voice.voice_id} disabled={Boolean(avatarVoiceExpression && voice.gender_expression && voice.gender_expression !== avatarVoiceExpression)}>
                  {voice.name} - {voice.tone} - {voice.gender_expression === "feminine" ? "woman" : voice.gender_expression === "masculine" ? "man" : "neutral"}
                </option>
              ))}
            </select>
          </Field>
          {selectedVoiceProfile && (
            <div style={voiceBox}>
              <strong>{selectedVoiceProfile.texture}</strong>
              <span>{selectedVoiceProfile.pace} pace / {selectedVoiceProfile.pitch} pitch / {selectedVoiceProfile.energy} energy</span>
              <span>The selected style changes delivery; the active avatar keeps its own stable voice identity.</span>
              <span>{voiceRuntime?.available ? "OmniMark Voice runtime connected" : "OmniMark Voice profile ready. Local speech runtime is not installed on this server yet."}</span>
              <span>{voiceRuntime?.commercialMusicAvailable ? "OmniMark in-house music composition and mastering connected" : "OmniMark in-house music runtime is unavailable."}</span>
            </div>
          )}
          <div style={tripleGrid}>
            <Field label="Shot style">
              <select value={shotStyle} onChange={(e) => setShotStyle(e.target.value)} style={input}>
                {shotStyles.map((style) => <option key={style}>{style}</option>)}
              </select>
            </Field>
            <Field label="Lighting">
              <select value={lighting} onChange={(e) => setLighting(e.target.value)} style={input}>
                {lightingPresets.map((preset) => <option key={preset}>{preset}</option>)}
              </select>
            </Field>
            <Field label="Creative scene">
              <select value={background} onChange={(e) => setBackground(e.target.value)} style={input}>
                {backgroundPresets.map((preset) => <option key={preset}>{preset}</option>)}
              </select>
            </Field>
          </div>
          <div style={doubleGrid}>
            <Field label="Avatar performance">
              <select value={performanceStyle} onChange={(e) => setPerformanceStyle(e.target.value)} style={input}>
                {performancePresets.map((preset) => <option key={preset}>{preset}</option>)}
              </select>
            </Field>
            <Field label="Custom scenery direction (optional)">
              <input value={customScene} onChange={(e) => setCustomScene(e.target.value)} placeholder="Example: sunny backyard with a family in soft focus" style={input} />
            </Field>
          </div>
          <div style={cinematicChecklistBox}>
            <strong>OmniScene Director</strong>
            <p style={{ margin: "6px 0 10px" }}>Describe mood, feeling, scenery, movement, blocking, and camera language. The director improves the visual plan only—this text is never used as spoken dialogue.</p>
            <Field label="Scene direction (visual only)">
              <textarea value={sceneBrief} onChange={(e) => setSceneBrief(e.target.value)} placeholder="Example: A joyful creator walks slowly through a sunlit neighborhood market, notices a product display, smiles naturally, and ends in a calm medium shot. Keep hands low and open; use a gentle lateral camera track." style={textareaTall} />
            </Field>
            <div style={actions}>
              {sceneMicStatus === "listening" ? <button type="button" onClick={stopSceneDictation} style={outlineButton}>Stop listening</button> : <button type="button" onClick={requestSceneDictation} style={outlineButton}>Use OmniMark microphone</button>}
              <button type="button" onClick={directScene} disabled={directorBusy} style={button}>{directorBusy ? "Directing scene..." : "Direct my scene"}</button>
            </div>
            {sceneMicPermissionPending && (
              <div role="dialog" aria-label="OmniMark microphone permission" style={{ ...voiceBox, marginTop: 12 }}>
                <strong>OmniMark microphone permission</strong>
                <span>OmniMark will use your microphone only to turn this scene direction into text in this browser. Audio is not sent for avatar voice cloning and is not added to the video.</span>
                <div style={actions}>
                  <button type="button" onClick={() => { setSceneMicPermissionPending(false); startSceneDictation(); }} style={button}>Continue to microphone permission</button>
                  <button type="button" onClick={() => setSceneMicPermissionPending(false)} style={outlineButton}>Not now</button>
                </div>
              </div>
            )}
            <div style={muted}>A fresh research-backed director pass runs when you choose “Direct my scene.” It proposes a scene plan and sources; it does not silently browse, publish, spend credits, or change your dialogue.</div>
            {directorPlan && <div style={{ ...voiceBox, marginTop: 12 }}><strong>{directorPlan.title || "Director plan"}</strong><span>{directorPlan.summary}</span><span><b>Voice/tone check:</b> {directorPlan.tone_check || "Use the selected avatar's stable voice identity."}</span>{Array.isArray(directorPlan.quality_checks) && directorPlan.quality_checks.length > 0 && <span><b>Quality checks:</b> {directorPlan.quality_checks.join(" • ")}</span>}{Array.isArray(directorPlan.suggestions) && directorPlan.suggestions.length > 0 && <span><b>Suggestions:</b> {directorPlan.suggestions.join(" • ")}</span>}{Array.isArray(directorPlan.sources) && directorPlan.sources.length > 0 && <span><b>Research sources:</b> {directorPlan.sources.slice(0, 3).join(" • ")}</span>}</div>}
          </div>
          {renderMode === "full_body" && (
            <div style={tripleGrid}>
              <Field label="Motion performance">
                <select value={motionProfile} onChange={(e) => setMotionProfile(e.target.value)} style={input}>
                  {motionProfiles.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </Field>
              <Field label="Gesture intensity">
                <select value={gestureIntensity} onChange={(e) => setGestureIntensity(e.target.value)} style={input}>
                  {gestureIntensities.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Locomotion">
                <select value={locomotion} onChange={(e) => setLocomotion(e.target.value)} style={input}>
                  {locomotionPresets.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
                </select>
              </Field>
              <Field label="Camera movement">
                <select value={cameraMotion} onChange={(e) => setCameraMotion(e.target.value)} style={input}>
                  {cameraMotionPresets.map((value) => <option key={value} value={value}>{value.replaceAll("_", " ")}</option>)}
                </select>
              </Field>
            </div>
          )}
          <div style={productionNote}>{renderMode === "full_body" ? "OmniMotion creates head-to-toe performance shots and checks duration, visible frames, lower-body motion, and freeze ratio before charging credits. Walking, running, and complex action scenes remain under visual quality review until each result passes." : renderMode === "cinematic" ? "Cinematic mode assembles editorial scene shots to the selected duration. It is reviewed for visual quality and does not guarantee natural full-body locomotion." : "Portrait mode is the production speech path for face, head, and voice animation; it does not create a new environment or full-body acting."}</div>
          {renderMode === "cinematic" && (
            <div style={cinematicChecklistBox}>
              <strong>Cinematic production check</strong>
              <ul style={checklist}>
                {cinematicQualityChecklist.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
          )}
          <Field label="Captions, graphics, and safe zones">
            <textarea value={captionPlan} onChange={(e) => setCaptionPlan(e.target.value)} style={textarea} />
          </Field>
          <Field label="Spoken dialogue only">
            <textarea value={script} onChange={(e) => setScript(e.target.value)} placeholder="Write only the words the avatar should say. Scene direction belongs above and will never be turned into dialogue." style={textareaTall} />
          </Field>
          <div style={productionNote}>The avatar uses its stable OmniMark voice identity and matching delivery style. A customer’s original recorded voice remains with that customer’s camera footage; it is not copied onto an avatar.</div>
          <Field label="OmniMark AI brief">
            <textarea value={studioBrief} readOnly style={textareaTall} />
          </Field>
          <div style={actions}>
            <button type="button" onClick={copyBrief} style={button}>Copy Brief</button>
            <button type="button" onClick={createVideoJob} disabled={videoBusy || (renderMode === "full_body" && !voiceRuntime?.omnimotion?.available)} style={outlineButton}>
              {videoBusy ? "Generating..." : "Generate Avatar Video"}
            </button>
            <Link to="/app/ads" onClick={() => sendToAdCreator()} style={outlineButton}>Use in Ad Creator</Link>
          </div>
        </div>
      </section>

      <section style={panel}>
        <div style={sectionHeader}>
          <div>
            <div style={sectionTitle}>Choose Real In-house Avatar</div>
            <div style={muted}>These are your generated OmniMark avatars. Pick one to preview, make active, or use for video briefs.</div>
          </div>
          <button type="button" onClick={() => handleUseAvatar()} disabled={!selectedAvatar} style={button}>Use Selected Avatar</button>
        </div>
        <div style={avatarGrid}>
          {avatars.map((avatar) => {
            const image = avatar.avatar_image_url || avatar.image_url || "";
            const active = selectedAvatar?.id === avatar.id;
            return (
              <button key={avatar.id} type="button" onClick={() => setSelectedAvatar(avatar)} style={active ? avatarCardActive : avatarCard}>
                {image ? (
                  <img src={image} alt={avatar.name} style={avatarImage} loading="lazy" decoding="async" />
                ) : (
                  <div style={emptyAvatar}>{avatar.name?.slice(0, 1) || "A"}</div>
                )}
                <strong>{avatar.name}</strong>
                <span style={muted}>Tone: {avatar.voice_tone || "-"}</span>
                <span style={muted}>Style: {avatar.look_style || "-"}</span>
                <div style={cardFooter}>
                  <span style={pill}>{avatar.source || "omnimark_generated"}</span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUseAvatar(avatar);
                    }}
                    style={miniButton}
                  >
                    Use
                  </button>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section style={compact ? mainGridCompact : mainGrid}>
        <div style={panel} ref={recentJobsRef}>
          <div style={sectionTitle}>Recent Studio Jobs</div>
          {jobs.length === 0 ? (
            <div style={muted}>No avatar video jobs yet. Create one from the studio brief panel.</div>
          ) : (
            <div style={jobList}>
              {jobs.map((job) => (
                <div key={job.id} style={jobCard}>
                  <div style={jobHeader}>
                    <div style={jobTitleGroup}>
                      <strong>{job.presenter_key || "OmniMark avatar"}</strong>
                      <span style={muted}>{job.status}</span>
                    </div>
                    <button type="button" onClick={() => deleteVideoJob(job.id)} style={dangerMiniButton}>
                      Delete
                    </button>
                  </div>
                  {(() => {
                    const playbackUrl = job.playback_url || job.output?.omnimark_video_url || job.output?.video_url;
                    const jobError = readableJobError(job.output?.error || job.output?.message);
                    return playbackUrl ? (
                    <>
                      <SoundVideo src={playbackUrl} style={jobVideo} />
                      {job.output?.omnimark_video_url && <span style={storagePill}>OmniMark optimized</span>}
                      {job.output?.voice && (
                        <span style={job.output.has_voice_audio ? storagePill : voicePill}>
                          Voice: {job.output.voice.voice_profile?.name || job.input?.voice_profile?.name || "OmniMark"} / {job.output.voice.status}
                        </span>
                      )}
                      <div style={toolLabel}>Video tools</div>
                      <div style={actions}>
                        <a href={playbackUrl} target="_blank" rel="noreferrer" style={miniButton}>Open Video</a>
                        <Link to="/app/ads" onClick={() => sendToAdCreator(job)} style={outlineMiniButton}>Use in Ad Creator</Link>
                        <Link to="/app/studio" onClick={() => sendToAdCreator(job)} style={outlineMiniButton}>Open AI Studio</Link>
                        <Link to="/app/campaigns" onClick={() => sendToAdCreator(job)} style={outlineMiniButton}>Attach to Campaign</Link>
                        <button type="button" onClick={() => syncVideoJob(job.id)} style={outlineMiniButton}>Refresh</button>
                        <button type="button" onClick={() => deleteVideoJob(job.id)} style={dangerMiniButton}>Delete</button>
                      </div>
                    </>
                    ) : (
                    <div style={jobError ? failedBox : processingBox}>
                      <span>{jobError || "Processing in OmniMark"}</span>
                      <div style={actions}>
                        <button type="button" onClick={() => syncVideoJob(job.id)} style={outlineMiniButton}>
                          {job.provider === "omnimark_in_house" ? "Check status" : "Refresh status"}
                        </button>
                        <button type="button" onClick={() => deleteVideoJob(job.id)} style={dangerMiniButton}>Delete</button>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={panel}>
          <div style={sectionTitle}>Studio Handoff</div>
          <div style={handoffGrid}>
            <Link to="/app/studio" style={handoffCard}>Open AI Studio</Link>
            <Link to="/app/ads" style={handoffCard}>Create Ad Pack</Link>
            <Link to="/app/campaigns" style={handoffCard}>Attach to Campaign</Link>
            <Link to="/app/dropshipping" style={handoffCard}>Use for Product Demo</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={field}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const page = { maxWidth: 1220, margin: "0 auto", display: "grid", gap: 16 };
const hero = { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(300px,.62fr)", gap: 18, alignItems: "center", padding: 22, borderRadius: 8, background: "linear-gradient(135deg,#07111f,#12372f 56%,#2d1b4e)", color: "#fff", overflow: "hidden" };
const heroCompact = { ...hero, gridTemplateColumns: "1fr", padding: 16 };
const heroCopy = { display: "grid", gap: 10 };
const eyebrow = { fontSize: 12, textTransform: "uppercase", letterSpacing: 0, color: "#a7f3d0", fontWeight: 900 };
const title = { margin: 0, fontSize: 36, lineHeight: 1.06 };
const subtitle = { margin: 0, color: "rgba(255,255,255,.78)", fontSize: 14, maxWidth: 760 };
const previewHero = { display: "grid", gap: 10, minWidth: 0 };
const heroImage = { width: "100%", maxHeight: 320, objectFit: "contain", borderRadius: 8, background: "rgba(255,255,255,.08)" };
const emptyPortrait = { height: 260, borderRadius: 8, display: "grid", placeItems: "center", background: "rgba(255,255,255,.12)", color: "#fff", fontSize: 44, fontWeight: 900 };
const heroAvatarInfo = { display: "grid", gap: 4, color: "rgba(255,255,255,.82)", fontSize: 13 };
const mainGrid = { display: "grid", gridTemplateColumns: "minmax(0,1.12fr) minmax(330px,.88fr)", gap: 16 };
const mainGridCompact = { ...mainGrid, gridTemplateColumns: "1fr" };
const panel = { borderRadius: 8, border: "1px solid rgba(0,0,0,.1)", background: "#fff", padding: 16, minWidth: 0 };
const workspacePanel = { ...panel, borderColor: "#bbf7d0", background: "#f0fdf4" };
const workspaceGrid = { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(300px,.9fr)", gap: 12 };
const workspaceGridCompact = { ...workspaceGrid, gridTemplateColumns: "1fr" };
const workspaceBriefBox = { border: "1px solid #bbf7d0", borderRadius: 8, background: "#fff", padding: 12, minWidth: 0 };
const briefPre = { whiteSpace: "pre-wrap", color: "#475467", fontFamily: "inherit", fontSize: 13, lineHeight: 1.35, margin: "8px 0 0" };
const sectionHeader = { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start", marginBottom: 12, flexWrap: "wrap" };
const sectionTitle = { fontSize: 20, fontWeight: 900, marginBottom: 6 };
const muted = { color: "#667085", fontSize: 13, lineHeight: 1.35 };
const actions = { display: "flex", flexWrap: "wrap", gap: 8 };
const button = { border: "none", borderRadius: 8, background: "#111827", color: "#fff", padding: "10px 12px", fontWeight: 900, cursor: "pointer", textDecoration: "none" };
const outlineButton = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, background: "#fff", color: "#111827", padding: "10px 12px", fontWeight: 900, cursor: "pointer", textDecoration: "none" };
const outlineButtonDark = { border: "1px solid rgba(255,255,255,.28)", borderRadius: 8, background: "rgba(255,255,255,.1)", color: "#fff", padding: "10px 12px", fontWeight: 900, cursor: "pointer", textDecoration: "none" };
const errorBox = { border: "1px solid #fecaca", background: "#fef2f2", color: "#991b1b", borderRadius: 8, padding: 12 };
const noticeBox = { border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#166534", borderRadius: 8, padding: 12 };
const modeBanner = { display: "grid", gap: 4, border: "1px solid #bbf7d0", background: "#f0fdf4", color: "#14532d", borderRadius: 8, padding: 10, fontSize: 13, margin: "8px 0 12px" };
const voiceBox = { display: "grid", gap: 4, border: "1px solid #c7d2fe", background: "#eef2ff", color: "#312e81", borderRadius: 8, padding: 10, fontSize: 13, margin: "8px 0 12px" };
const presetGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8, marginBottom: 14 };
const presetCard = { display: "grid", gap: 5, textAlign: "left", border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#f8fafc", padding: 10, cursor: "pointer", fontSize: 13 };
const formGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 };
const wideFormGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 10, marginTop: 10 };
const tripleGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8 };
const doubleGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 8, marginTop: 8 };
const productionNote = { border: "1px solid #fde68a", background: "#fffbeb", color: "#854d0e", borderRadius: 8, padding: 10, fontSize: 12, lineHeight: 1.4, margin: "10px 0" };
const cinematicChecklistBox = { border: "1px solid #c7d2fe", background: "#eef2ff", color: "#312e81", borderRadius: 8, padding: 12, fontSize: 12, lineHeight: 1.45, margin: "10px 0" };
const checklist = { margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 4 };
const field = { display: "grid", gap: 6, fontSize: 13, fontWeight: 800 };
const input = { width: "100%", borderRadius: 8, border: "1px solid rgba(0,0,0,.14)", padding: "10px 12px", fontSize: 14, fontFamily: "inherit", background: "#fff" };
const selectInput = { ...input, width: "auto", minWidth: 210 };
const textarea = { ...input, minHeight: 104, resize: "vertical" };
const textareaTall = { ...input, minHeight: 130, resize: "vertical" };
const avatarGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(230px,1fr))", gap: 12 };
const avatarCard = { display: "grid", gap: 8, textAlign: "left", border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#fff", padding: 12, cursor: "pointer" };
const avatarCardActive = { ...avatarCard, borderColor: "#0f766e", boxShadow: "0 0 0 2px rgba(15,118,110,.16)", background: "#ecfdf5" };
const avatarImage = { width: "100%", height: 230, objectFit: "contain", borderRadius: 8, background: "#f3f4f6" };
const emptyAvatar = { height: 230, display: "grid", placeItems: "center", borderRadius: 8, background: "linear-gradient(135deg,#111827,#14b8a6)", color: "#fff", fontSize: 42, fontWeight: 900 };
const cardFooter = { display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" };
const pill = { width: "fit-content", borderRadius: 999, background: "#eef6ff", padding: "3px 8px", fontSize: 11, fontWeight: 800 };
const storagePill = { width: "fit-content", borderRadius: 999, background: "#dcfce7", color: "#166534", padding: "3px 8px", fontSize: 11, fontWeight: 900 };
const voicePill = { width: "fit-content", borderRadius: 999, background: "#eef2ff", color: "#3730a3", padding: "3px 8px", fontSize: 11, fontWeight: 900 };
const miniButton = { border: "none", borderRadius: 8, background: "#111827", color: "#fff", padding: "7px 9px", fontSize: 12, fontWeight: 900, cursor: "pointer" };
const outlineMiniButton = { border: "1px solid rgba(0,0,0,.14)", borderRadius: 8, background: "#fff", color: "#111827", padding: "7px 9px", fontSize: 12, fontWeight: 900, cursor: "pointer" };
const dangerMiniButton = { border: "1px solid #fecaca", borderRadius: 8, background: "#fff", color: "#991b1b", padding: "7px 9px", fontSize: 12, fontWeight: 900, cursor: "pointer" };
const jobList = { display: "grid", gap: 8 };
const jobCard = { display: "grid", gap: 6, border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#f8fafc", padding: 10 };
const jobHeader = { display: "flex", alignItems: "start", justifyContent: "space-between", gap: 10 };
const jobTitleGroup = { display: "grid", gap: 3, minWidth: 0 };
const jobVideo = { width: "100%", borderRadius: 8, border: "1px solid rgba(0,0,0,.1)", aspectRatio: "16 / 9", background: "#020617" };
const toolLabel = { color: "#344054", fontSize: 12, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0 };
const processingBox = { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap", borderRadius: 8, background: "#111827", color: "#fff", padding: 10, fontSize: 13 };
const failedBox = { ...processingBox, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", lineHeight: 1.35 };
const handoffGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8 };
const handoffCard = { border: "1px solid rgba(0,0,0,.1)", borderRadius: 8, background: "#f8fafc", color: "#111827", padding: 12, textDecoration: "none", fontWeight: 900 };
